import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { dispatch } from 'redux/store';
import {
  CampaignMember,
  CampaignMemberCategory,
  campaignMemberCategoryAditionStatusMap,
  CampaignMembersData,
  CampaignMemberState,
  Email,
  Sms
} from '../../@types/campaignMember';
import { Note } from '../../@types/note';
import { GridFilterModel, GridLogicOperator, GridSortModel } from '@mui/x-data-grid';
import { getFilterQueryParam, getOrderbyQueryParam } from 'utils/getQueryParams';
import { translator } from 'translation/useTranslator';
import { rejectWithError } from '../dispatchError';

const initialCampaignMembersData: CampaignMembersData = {
  take: 0,
  skip: 0,
  campaignMembers: [],
  total: 0
};
const initialState: CampaignMemberState = {
  isLoading: {},
  campaignMember: undefined,
  notes: [],
  activities: [],
  campaignMembersData: {
    [CampaignMemberCategory.RESPONSES]: initialCampaignMembersData,
    [CampaignMemberCategory.DELIVERED]: initialCampaignMembersData,
    [CampaignMemberCategory.SCHEDULED]: initialCampaignMembersData,
    [CampaignMemberCategory.UNSUBSCRIBED]: initialCampaignMembersData,
    [CampaignMemberCategory.FAILED]: initialCampaignMembersData,
    [CampaignMemberCategory.EXPIRED]: initialCampaignMembersData
  },
  updateCampaignMembers: 0
};

const slice = createSlice({
  name: 'campaignMember',
  initialState,
  reducers: {
    startLoading(state, action) {
      state.isLoading[action.payload] = true;
    },
    stopLoading(state, action) {
      state.isLoading[action.payload] = false;
    },
    setCampaignMembers(state, action) {
      const { campaignMembersData, campaignMemberCategory } = action.payload;
      state.campaignMembersData[campaignMemberCategory as CampaignMemberCategory] =
        campaignMembersData;
      state.isLoading[`getCampaignMembers${campaignMemberCategory}`] = false;
    },
    setCampaignMember(state, action) {
      state.campaignMember = action.payload;
      state.isLoading.getCampaignMember = false;
    },
    setNotes(state, action) {
      const newNotes = action.payload.notes as Note[];
      state.notes = newNotes.toSorted((a, b) => {
        const timeA = new Date(a.created).getTime();
        const timeB = new Date(b.created).getTime();
        return timeA - timeB;
      });
      state.isLoading.getNotes = false;

      const campaignMemberId = action.payload.campaignMemberId;
      if (campaignMemberId) {
        //update the notes of the relevant campaignMember, in campaignMembersData
        for (const categoryIndex in CampaignMemberCategory) {
          const category =
            CampaignMemberCategory[categoryIndex as keyof typeof CampaignMemberCategory];

          const relevantCampaignMember = state.campaignMembersData[
            category
          ]?.campaignMembers?.find?.(
            (campaignMember: CampaignMember) => campaignMember.id === campaignMemberId
          );
          if (relevantCampaignMember) {
            //update entry with new notes
            state.campaignMembersData[category].campaignMembers = state.campaignMembersData[
              category
            ].campaignMembers.map((campaignMember: CampaignMember) => {
              if (campaignMember.id === relevantCampaignMember.id) {
                return { ...campaignMember, notes: newNotes };
              }
              return campaignMember;
            });

            // Exit the loop after the first hit is found
            break;
          }
        }
      }
    },
    setActivities(state, action) {
      state.activities = action.payload;
      state.isLoading.getActivities = false;
    },
    onDeleteCampaignMemberSuccess(state) {
      state.campaignMember = initialState.campaignMember;
      state.isLoading.deleteCampaignMember = false;
    },
    campaignMemebersShouldUpdate(state) {
      state.updateCampaignMembers = state.updateCampaignMembers + 1;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { campaignMemebersShouldUpdate, setCampaignMember, setActivities } = slice.actions;

// ----------------------------------------------------------------------

//return the campaignmember with 4 extra props: surveySendTime, surveyScheduledTime, reminderSendTime, and reminderScheduledTime
const getModifiedCampaignMember = (campaignMember: CampaignMember): CampaignMember => {
  const emails = campaignMember?.emails;
  const smses = campaignMember?.sms;
  let contactEntries: Email[] | Sms[] = [];
  if (emails?.length) {
    contactEntries = emails;
  } else if (smses?.length) {
    contactEntries = smses;
  }

  let surveySendTime = null;
  let surveyScheduledTime = null;
  let reminderScheduledTime = null;
  let reminderSendTime = null;
  if (contactEntries?.length) {
    //reverse entrie, so first index is the last entry
    const reversedContactEntries: (Email | Sms)[] = contactEntries.reverse();

    //survey sent, survey scheduled
    const newestMainEntry = reversedContactEntries.find((entry: any) => {
      const status = entry.emailStatus ?? entry.smsStatus;
      const type = entry.emailType ?? entry.smsType;
      return (
        (status === 'Scheduled' ||
          status === 'Added' ||
          status === 'Sent' ||
          status === 'Opened' ||
          status === 'Delivered' ||
          status === 'Bounced' ||
          status === 'Clicked' ||
          status === 'CancelledInactive' ||
          status === 'CancelledDoNotContact' ||
          status === 'CancelledEndedCampaign' ||
          status === 'CancelledReminder' ||
          status === 'Cancelled' ||
          status === 'CancelledThrottle' ||
          status === 'NotCapableOfReceivingSMS') &&
        (type === 'SurveyMail' || type === 'Survey' || type === 'Delivered')
      );
    });
    surveySendTime = newestMainEntry?.sendTime;
    surveyScheduledTime = newestMainEntry?.scheduledTime;

    //reminder sent, reminder scheduled
    const newestReminderEntry = reversedContactEntries.find((entry: any) => {
      const status = entry.emailStatus ?? entry.smsStatus;
      const type = entry.emailType ?? entry.smsType;
      return (
        (status === 'Scheduled' ||
          status === 'Added' ||
          status === 'Sent' ||
          status === 'Opened' ||
          status === 'Delivered' ||
          status === 'Bounced' ||
          status === 'Clicked' ||
          status === 'CancelledInactive' ||
          status === 'CancelledDoNotContact' ||
          status === 'CancelledEndedCampaign' ||
          status === 'CancelledReminder' ||
          status === 'Cancelled' ||
          status === 'CancelledThrottle' ||
          status === 'NotCapableOfReceivingSMS') &&
        (type === 'Reminder' || type === 'ManualResend')
      );
    });
    reminderSendTime = newestReminderEntry?.sendTime;
    reminderScheduledTime = newestReminderEntry?.scheduledTime;
  }

  return {
    ...campaignMember,
    surveySendTime: surveySendTime ?? undefined,
    surveyScheduledTime: surveyScheduledTime ?? undefined,
    reminderScheduledTime: reminderScheduledTime ?? undefined,
    reminderSendTime: reminderSendTime ?? undefined
  };
};

type GetCampaignMembersProps = {
  page?: number;
  take: number;
  campaignId: string;
  awaiting: boolean;
  sortModel: GridSortModel | undefined;
  filterModel: GridFilterModel | undefined;
  from?: string;
  to?: string;
  campaignMemberCategory: CampaignMemberCategory;
};

// When the grid filters on the synthetic "responsibleEmployee" column, expand it
// into an OData filter where every entered word must match firstName, lastName, or email.
function buildResponsibleEmployeeFilter(filterModel: GridFilterModel | undefined): string {
  const responsibleEmployeeFilterItem = filterModel?.items.find(
    (filterItem) => filterItem.field === 'responsibleEmployee'
  );
  if (!responsibleEmployeeFilterItem?.value) {
    return '';
  }
  return responsibleEmployeeFilterItem.value
    .split(' ')
    .filter(Boolean)
    .map(
      (word: string) =>
        `(${getFilterQueryParam(
          {
            items: [
              {
                field: 'responsibleEmployee/firstName',
                operator: 'contains',
                value: word
              },
              {
                field: 'responsibleEmployee/lastName',
                operator: 'contains',
                value: word
              },
              {
                field: 'responsibleEmployee/email',
                operator: 'contains',
                value: word
              }
            ],
            logicOperator: GridLogicOperator.Or
          },
          true,
          true
        )})`
    )
    .join(' and ');
}

// The synthetic "responsibleEmployee" sort column expands to firstName, lastName,
// then email (in that order); other sort items are kept as-is.
function buildCampaignMemberSortModel(sortModel: GridSortModel | undefined): GridSortModel {
  const updatedSortModel =
    sortModel?.filter((sortModelItem) => sortModelItem.field !== 'responsibleEmployee') ?? [];
  const responsibleEmployeeSortItem = sortModel?.find(
    (sortModelItem) => sortModelItem.field === 'responsibleEmployee'
  );
  if (!responsibleEmployeeSortItem) {
    return updatedSortModel;
  }
  return [
    ...updatedSortModel,
    { ...responsibleEmployeeSortItem, field: 'responsibleEmployee/firstName' },
    { ...responsibleEmployeeSortItem, field: 'responsibleEmployee/lastName' },
    { ...responsibleEmployeeSortItem, field: 'responsibleEmployee/email' }
  ];
}

export const getCampaignMembers = ({
  page,
  take,
  campaignId,
  awaiting,
  sortModel,
  filterModel,
  from,
  to,
  campaignMemberCategory
}: GetCampaignMembersProps) => {
  return async () => {
    const isLoadingIndex = `getCampaignMembers${campaignMemberCategory}`;
    dispatch(slice.actions.startLoading(isLoadingIndex));
    try {
      const aditionStatusRange = campaignMemberCategoryAditionStatusMap.get(campaignMemberCategory);
      const adtitionStatusMinFilter =
        typeof aditionStatusRange?.min === 'number'
          ? `additionStatus ge ${aditionStatusRange?.min}`
          : '';
      const adtitionStatusMaxFilter =
        typeof aditionStatusRange?.max === 'number'
          ? `additionStatus le ${aditionStatusRange?.max}`
          : '';
      const aditionStatusExcludeFilters =
        aditionStatusRange?.exclude?.map((statusCode) => `additionStatus ne ${statusCode}`) ?? [];

      //ratedTime, used for Responses
      let fromDataFilterRatedTime = from ? `response/rated gt ${from}` : '';
      let toDataFilterRatedTime = to ? `response/rated lt ${to}` : '';
      //firstSurveyExposure, used for Delivered, Scheduled and Failed
      let fromDataFilterFirstSurveyExposure = from ? `firstSurveyExposure gt ${from}` : '';
      let toDataFilterFirstSurveyExposure = to ? `firstSurveyExposure lt ${to}` : '';

      /**
       * FILTER
       */
      const campaignMemberFilterItems = [`campaignId eq ${campaignId}`];

      //responsibleEmployee filter logic is handled below, so remove if present
      let updatedFilterModel = filterModel
        ? {
            ...filterModel,
            items: filterModel.items.filter(
              (filterItem) => filterItem.field !== 'responsibleEmployee'
            )
          }
        : undefined;
      const responsibleEmployeeFilter = buildResponsibleEmployeeFilter(filterModel);
      const customFilter = getFilterQueryParam(updatedFilterModel, true, true);
      if (customFilter) {
        campaignMemberFilterItems.push(customFilter);
      }
      if (responsibleEmployeeFilter) {
        campaignMemberFilterItems.push(responsibleEmployeeFilter);
      }

      if (campaignMemberCategory === CampaignMemberCategory.RESPONSES) {
        campaignMemberFilterItems.push(
          `response ${awaiting ? 'eq' : 'ne'} null`,
          fromDataFilterRatedTime,
          toDataFilterRatedTime
        );
      } else {
        campaignMemberFilterItems.push(
          adtitionStatusMinFilter,
          adtitionStatusMaxFilter,
          ...aditionStatusExcludeFilters,
          fromDataFilterFirstSurveyExposure,
          toDataFilterFirstSurveyExposure
        );
      }

      const campaignMemberFilter = `$filter=${campaignMemberFilterItems
        .filter(Boolean)
        .join(' and ')}`;

      /**
       * ORDERBY
       */
      const updatedSortModel = buildCampaignMemberSortModel(sortModel);

      const skip = (page || 0) * take;
      const response = await axios.get(
        `/odata/campaignmembers?$count=true&$top=${take}&$skip=${skip}&${campaignMemberFilter}${getOrderbyQueryParam(
          updatedSortModel
        )}&$expand=response($expand=category),responsibleEmployee,respondent,campaign($expand=template),notes,emails,sms`
      );

      const campaignMembers =
        response.data.value?.map((campaignMember: CampaignMember) =>
          getModifiedCampaignMember(campaignMember)
        ) ?? [];

      const newCampaignMembersData = {
        take: take,
        skip: skip,
        campaignMembers: campaignMembers,
        total: response.data?.['@odata.count'] || 0
      };

      dispatch(
        slice.actions.setCampaignMembers({
          campaignMembersData: newCampaignMembersData,
          campaignMemberCategory: campaignMemberCategory
        })
      );
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.campaignMembersFetched() ?? 'Campaign members fetched'
      });
    } catch (error) {
      dispatch(
        slice.actions.setCampaignMembers({
          campaignMembersData: [],
          campaignMemberCategory: campaignMemberCategory
        })
      );
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchCampaignMembers() ?? 'Could not fetch campaign members'
      });
    }
  };
};

type GetCampaignMemberProps = {
  id: number;
};
export const getCampaignMember = ({ id }: GetCampaignMemberProps) => {
  return async () => {
    dispatch(slice.actions.startLoading('getCampaignMember'));
    try {
      const response = await axios.get(
        `/odata/campaignmembers(${id})?$expand=response($expand=category),responsibleEmployee,respondent($expand=company),campaign($expand=template,brandedLink)`
      );
      dispatch(slice.actions.setCampaignMember(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.campaignMemberFetched() ?? 'Campaign member fetched'
      });
    } catch (error) {
      dispatch(slice.actions.setCampaignMember(undefined));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchCampaignMember() ?? 'Could not fetch campaign member'
      });
    }
  };
};

type EditCampaignMemberProps = {
  campaignMemberId: string;
  updatedCampaignMember: Partial<CampaignMember>;
};
export const editCampaignMember = ({
  campaignMemberId,
  updatedCampaignMember
}: EditCampaignMemberProps) => {
  return async () => {
    dispatch(slice.actions.startLoading('editCampaignMember'));
    try {
      const response = await axios.put(
        `/odata/campaignmembers(${campaignMemberId})`,
        updatedCampaignMember
      );
      dispatch(slice.actions.stopLoading('editCampaignMember'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.campaignMemberUpdated() ?? 'Campaign member updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('editCampaignMember'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUpdateCampaignMember() ?? 'Could not update campaign member'
      });
    }
  };
};

type DeleteCampaignMemberProps = {
  campaignMember: CampaignMember;
};
export function deleteCampaignMember({ campaignMember }: DeleteCampaignMemberProps) {
  return async () => {
    dispatch(slice.actions.startLoading('deleteCampaignMember'));
    await axios
      .delete(`campaigns/${campaignMember.campaignId}/members/${campaignMember.id}`)
      .catch((error: any) => {
        dispatch(slice.actions.stopLoading('deleteCampaignMember'));
        return rejectWithError({
          error: error,
          defaultErrorMessage:
            translator?.couldNotDeleteCampaignMember() ?? 'Could not be delete campaign member'
        });
      });
    dispatch(slice.actions.onDeleteCampaignMemberSuccess());
    return {
      defaultSuccessMessage: translator?.campaignMemberDeleted() ?? 'Campaignmember deleted'
    };
  };
}

type GetNotesProps = {
  campaignMemberId: number;
};
export const getNotes = ({ campaignMemberId }: GetNotesProps) => {
  return async () => {
    dispatch(slice.actions.startLoading('getNotes'));
    try {
      const response = await axios.get(`/v2/campaignMembers/${campaignMemberId}/notes`);
      dispatch(
        slice.actions.setNotes({ notes: response.data, campaignMemberId: campaignMemberId })
      );
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.notesFetched() ?? 'Notes fetched'
      });
    } catch (error) {
      dispatch(slice.actions.setNotes({ notes: [], campaignMemberId: campaignMemberId }));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchNotes() ?? 'Could not fetch notes'
      });
    }
  };
};

type CreateNoteProps = {
  campaignMemberId: number;
  text: string;
};
export const createNote = ({ campaignMemberId, text }: CreateNoteProps) => {
  return async () => {
    dispatch(slice.actions.startLoading('createNote'));
    try {
      const response = await axios.post(`/v2/campaignMembers/${campaignMemberId}/notes`, {
        note: text
      });
      dispatch(slice.actions.stopLoading('createNote'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.noteAdded() ?? 'Note added'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('createNote'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotAddNote() ?? 'Could not add note'
      });
    }
  };
};

type EditNoteProps = {
  campaignMemberId: number;
  noteId: string;
  text: string;
};
export const editNote = ({ campaignMemberId, noteId, text }: EditNoteProps) => {
  return async () => {
    const isLoadingIndex = `editNote${noteId}`;
    dispatch(slice.actions.startLoading(isLoadingIndex));
    try {
      const response = await axios.put(`/v2/campaignMembers/${campaignMemberId}/notes/${noteId}`, {
        note: text
      });
      dispatch(slice.actions.stopLoading(isLoadingIndex));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.noteUpdated() ?? 'Note updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading(isLoadingIndex));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUpdateNote() ?? 'Could not update note'
      });
    }
  };
};

type DeleteNoteProps = {
  campaignMemberId: number;
  noteId: string;
};
export const deleteNote = ({ campaignMemberId, noteId }: DeleteNoteProps) => {
  return async () => {
    const isLoadingIndex = `deleteNote${noteId}`;
    dispatch(slice.actions.startLoading(isLoadingIndex));
    try {
      const response = await axios.delete(
        `/v2/campaignMembers/${campaignMemberId}/notes/${noteId}`
      );
      dispatch(slice.actions.stopLoading(isLoadingIndex));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.noteDeleted() ?? 'Note deleted'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading(isLoadingIndex));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotDeleteNote() ?? 'Could not delete note'
      });
    }
  };
};

type GetActivitiesProps = {
  campaignMemberId: string;
};
export function getActivities({ campaignMemberId }: GetActivitiesProps) {
  return async () => {
    dispatch(slice.actions.startLoading('getActivities'));
    try {
      const response = await axios.get(`/history/campaignMembers/${campaignMemberId}/activities`);
      dispatch(slice.actions.setActivities(response.data));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.historyFetched() ?? 'History fetched'
      });
    } catch (error) {
      dispatch(slice.actions.setActivities([]));

      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchHistory() ?? 'Could not fetch history'
      });
    }
  };
}
type RecordLoopActivityProps = {
  campaignMemberId: string;
  activity: 'Contacted' | 'Closed' | 'Reopened';
  text?: string;
};
export function recordLoopActivity({ campaignMemberId, activity, text }: RecordLoopActivityProps) {
  return async () => {
    dispatch(slice.actions.startLoading('recordLoopActivity'));
    try {
      await axios.post(`/history/campaignMembers/${campaignMemberId}/activities`, { activity, text });
      const response = await axios.get(`/history/campaignMembers/${campaignMemberId}/activities`);
      dispatch(slice.actions.setActivities(response.data));
      dispatch(slice.actions.stopLoading('recordLoopActivity'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.activityRecorded() ?? 'Activity recorded'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('recordLoopActivity'));

      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotRecordActivity() ?? 'Could not record the activity'
      });
    }
  };
}
